import React, { useState, useEffect } from 'react';
import { aiAPI, projectsAPI, ffeAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Sparkles, DollarSign, Store, Sofa, Loader2, CheckCircle2, AlertCircle, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

const STYLES = ['modern contemporary', 'minimalist', 'classical', 'art deco', 'scandinavian', 'industrial', 'bohemian', 'traditional indian', 'coastal', 'transitional'];
const TIERS = ['budget', 'mid-range', 'premium', 'luxury'];

export default function AIAssistant() {
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('budget');

  // Budget Estimation
  const [budgetForm, setBudgetForm] = useState({ project_id: '', style: 'modern contemporary', city: '', tier: 'mid-range' });
  const [budgetResult, setBudgetResult] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Vendor Recommendations
  const [vendorForm, setVendorForm] = useState({ item_description: '', category: '', budget_range: '' });
  const [vendorResult, setVendorResult] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  // FF&E Schedule
  const [ffeForm, setFfeForm] = useState({ project_id: '', style: 'modern contemporary', tier: 'mid-range' });
  const [ffeResult, setFfeResult] = useState(null);
  const [ffeLoading, setFfeLoading] = useState(false);
  const [savingFFE, setSavingFFE] = useState(false);

  useEffect(() => {
    projectsAPI.list().then(res => setProjects(res.data)).catch(console.error);
  }, []);

  // Budget Estimation
  const handleBudgetEstimate = async () => {
    if (!budgetForm.project_id) { toast.error('Select a project'); return; }
    setBudgetLoading(true);
    setBudgetResult(null);
    try {
      const project = projects.find(p => p.project_id === budgetForm.project_id);
      const res = await aiAPI.budgetEstimate({ ...budgetForm, city: project?.city || budgetForm.city || 'Mumbai' });
      if (res.data.status === 'success') {
        setBudgetResult(res.data.estimate);
      } else {
        toast.error('AI returned unexpected format. Check raw response.');
        setBudgetResult(null);
      }
    } catch (err) {
      toast.error('Budget estimation failed');
      console.error(err);
    } finally {
      setBudgetLoading(false);
    }
  };

  // Vendor Recommendations
  const handleVendorRecs = async () => {
    if (!vendorForm.item_description) { toast.error('Describe what you need'); return; }
    setVendorLoading(true);
    setVendorResult(null);
    try {
      const res = await aiAPI.vendorRecommendations(vendorForm);
      if (res.data.status === 'success') {
        setVendorResult(res.data.recommendations);
      } else {
        toast.error('AI returned unexpected format');
      }
    } catch (err) {
      toast.error('Vendor recommendation failed');
      console.error(err);
    } finally {
      setVendorLoading(false);
    }
  };

  // FF&E Schedule
  const handleGenerateFFE = async () => {
    if (!ffeForm.project_id) { toast.error('Select a project'); return; }
    setFfeLoading(true);
    setFfeResult(null);
    try {
      const res = await aiAPI.generateFFESchedule(ffeForm);
      if (res.data.status === 'success') {
        setFfeResult(res.data.schedule);
      } else {
        toast.error('AI returned unexpected format');
      }
    } catch (err) {
      toast.error('FF&E generation failed');
      console.error(err);
    } finally {
      setFfeLoading(false);
    }
  };

  const handleSaveFFEItems = async () => {
    if (!ffeResult?.schedule || !ffeForm.project_id) return;
    setSavingFFE(true);
    let saved = 0;
    try {
      // Get rooms to map names
      const roomsRes = await import('../lib/api').then(m => m.roomsAPI.list(ffeForm.project_id));
      const roomMap = {};
      for (const r of roomsRes.data) {
        roomMap[r.name.toLowerCase()] = r.room_id;
      }

      for (const roomSchedule of ffeResult.schedule) {
        const roomId = roomMap[roomSchedule.room_name?.toLowerCase()] || '';
        for (const item of roomSchedule.items || []) {
          await ffeAPI.create({
            name: item.name,
            category: item.category || 'other',
            description: item.description || '',
            trade_price: item.estimated_trade_price || 0,
            markup_percentage: item.suggested_markup || 30,
            quantity: item.quantity || 1,
            status: 'sourcing',
            project_id: ffeForm.project_id,
            room_id: roomId,
          });
          saved++;
        }
      }
      toast.success(`Saved ${saved} FF&E items to library`);
    } catch (err) {
      toast.error(`Saved ${saved} items, then failed: ${err.message}`);
    } finally {
      setSavingFFE(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="ai-assistant-page">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-amber-500" />AI Assistant
          </h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered tools for budget estimation, vendor recommendations, and FF&E generation</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="budget" data-testid="ai-tab-budget"><DollarSign className="h-4 w-4 mr-2" />Budget Estimate</TabsTrigger>
            <TabsTrigger value="vendor" data-testid="ai-tab-vendor"><Store className="h-4 w-4 mr-2" />Vendor Recommendations</TabsTrigger>
            <TabsTrigger value="ffe" data-testid="ai-tab-ffe"><Sofa className="h-4 w-4 mr-2" />Auto FF&E Schedule</TabsTrigger>
          </TabsList>

          {/* ===== BUDGET ESTIMATION ===== */}
          <TabsContent value="budget" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Budget Estimation</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">AI estimates budget based on your project rooms, design style, and tier. Make sure rooms are defined in the project first.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={budgetForm.project_id || 'none'} onValueChange={v => setBudgetForm({ ...budgetForm, project_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="ai-budget-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Select project</SelectItem>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Style</Label>
                    <Select value={budgetForm.style} onValueChange={v => setBudgetForm({ ...budgetForm, style: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STYLES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Budget Tier</Label>
                    <Select value={budgetForm.tier} onValueChange={v => setBudgetForm({ ...budgetForm, tier: v })}>
                      <SelectTrigger data-testid="ai-budget-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIERS.map(t => <SelectItem key={t} value={t}><span className="capitalize">{t}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleBudgetEstimate} disabled={budgetLoading} className="w-full" data-testid="ai-budget-generate">
                      {budgetLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {budgetLoading ? 'Estimating...' : 'Generate Estimate'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {budgetResult && (
              <Card data-testid="ai-budget-result">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Budget Estimate — {fmt(budgetResult.total_estimated)}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{budgetResult.summary}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(budgetResult.rooms || []).map((room, idx) => (
                    <div key={idx} className="border rounded p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{room.room_name}</h4>
                        <span className="text-sm font-medium">{fmt(room.room_total)}</span>
                      </div>
                      <Table>
                        <TableHeader><TableRow><TableHead className="text-xs">Category</TableHead><TableHead className="text-xs">Description</TableHead><TableHead className="text-xs text-right">Estimate</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {(room.items || []).map((item, i) => (
                            <TableRow key={i}>
                              <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded bg-muted">{item.category}</span></TableCell>
                              <TableCell className="text-sm">{item.description}</TableCell>
                              <TableCell className="text-right font-medium text-sm">{fmt(item.estimated_cost)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                  {budgetResult.notes && (
                    <div className="border-t pt-3">
                      <p className="text-sm font-medium mb-2">Tips</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {budgetResult.notes.map((note, i) => <li key={i} className="flex gap-2"><AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />{note}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== VENDOR RECOMMENDATIONS ===== */}
          <TabsContent value="vendor" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Vendor Recommendations</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">AI analyzes your vendor database and past orders to recommend the best vendors for your requirement.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>What are you looking for?</Label>
                    <Input value={vendorForm.item_description} onChange={e => setVendorForm({ ...vendorForm, item_description: e.target.value })} placeholder="e.g., Italian marble tiles for bathroom flooring" data-testid="ai-vendor-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Budget Range</Label>
                    <Input value={vendorForm.budget_range} onChange={e => setVendorForm({ ...vendorForm, budget_range: e.target.value })} placeholder="e.g., ₹50,000 - ₹1,00,000" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleVendorRecs} disabled={vendorLoading} className="w-full" data-testid="ai-vendor-generate">
                      {vendorLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {vendorLoading ? 'Analyzing...' : 'Get Recommendations'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {vendorResult && (
              <Card data-testid="ai-vendor-result">
                <CardContent className="p-0">
                  <div className="divide-y">
                    {(vendorResult.recommendations || []).map((rec, idx) => (
                      <div key={idx} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <Store className="h-4 w-4 text-muted-foreground" />{rec.vendor_name}
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${rec.confidence === 'high' ? 'bg-green-100 text-green-700' : rec.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{rec.confidence}</span>
                            </h4>
                            <p className="text-sm text-muted-foreground mt-1">{rec.reason}</p>
                          </div>
                          <p className="text-sm font-medium shrink-0">{rec.estimated_price_range}</p>
                        </div>
                        {rec.relevant_articles?.length > 0 && (
                          <div className="flex gap-2 flex-wrap mt-2">
                            {rec.relevant_articles.map((a, i) => <span key={i} className="text-xs px-2 py-0.5 bg-muted rounded">{a}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {vendorResult.tips?.length > 0 && (
                    <div className="p-4 border-t bg-muted/30">
                      <p className="text-xs font-medium mb-1">Procurement Tips</p>
                      <ul className="text-xs text-muted-foreground space-y-0.5">
                        {vendorResult.tips.map((t, i) => <li key={i}>- {t}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== FF&E SCHEDULE GENERATOR ===== */}
          <TabsContent value="ffe" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Auto-Generate FF&E Schedule</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">AI generates a complete FF&E schedule based on your project rooms and design preferences. You can then save all items to your FF&E Library.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={ffeForm.project_id || 'none'} onValueChange={v => setFfeForm({ ...ffeForm, project_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="ai-ffe-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Select project</SelectItem>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Style</Label>
                    <Select value={ffeForm.style} onValueChange={v => setFfeForm({ ...ffeForm, style: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STYLES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tier</Label>
                    <Select value={ffeForm.tier} onValueChange={v => setFfeForm({ ...ffeForm, tier: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TIERS.map(t => <SelectItem key={t} value={t}><span className="capitalize">{t}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleGenerateFFE} disabled={ffeLoading} className="w-full" data-testid="ai-ffe-generate">
                      {ffeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {ffeLoading ? 'Generating...' : 'Generate Schedule'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {ffeResult && (
              <Card data-testid="ai-ffe-result">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        FF&E Schedule — {ffeResult.total_items} items
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Trade: {fmt(ffeResult.estimated_total_trade)} | Client: {fmt(ffeResult.estimated_total_client)}
                      </p>
                      {ffeResult.style_notes && <p className="text-xs text-muted-foreground mt-1">{ffeResult.style_notes}</p>}
                    </div>
                    <Button onClick={handleSaveFFEItems} disabled={savingFFE} data-testid="ai-ffe-save-all">
                      {savingFFE ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      {savingFFE ? 'Saving...' : 'Save All to FF&E Library'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(ffeResult.schedule || []).map((room, idx) => (
                    <div key={idx} className="border rounded p-4">
                      <h4 className="font-medium mb-3">{room.room_name}</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Item</TableHead>
                            <TableHead className="text-xs">Category</TableHead>
                            <TableHead className="text-xs text-center">Qty</TableHead>
                            <TableHead className="text-xs text-right">Trade</TableHead>
                            <TableHead className="text-xs text-right">Markup</TableHead>
                            <TableHead className="text-xs text-right">Client</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(room.items || []).map((item, i) => {
                            const clientPrice = (item.estimated_trade_price || 0) * (1 + (item.suggested_markup || 30) / 100);
                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  <div><p className="text-sm font-medium">{item.name}</p>{item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}</div>
                                </TableCell>
                                <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded bg-muted">{item.category}</span></TableCell>
                                <TableCell className="text-center text-sm">{item.quantity}</TableCell>
                                <TableCell className="text-right text-sm">{fmt(item.estimated_trade_price)}</TableCell>
                                <TableCell className="text-right text-sm">{item.suggested_markup}%</TableCell>
                                <TableCell className="text-right text-sm font-medium text-green-600">{fmt(clientPrice * (item.quantity || 1))}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
